import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Formik } from "formik";
import { FaPlus } from "react-icons/fa";
import Select, { Option } from "../Select";
import { Slider } from "../Slider";
import { Box } from "../Box";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { H2, H3 } from "../Heading";
import { Input } from "../Input";
import { DatePicker } from "../DatePicker";
import { Paragraph } from "../Paragraph";
import { RouteLink } from "../RouteLink";
import { useBillingInfo } from "../../data/useBillingInfo";
import { useCommonTitles } from "../../data/useCommonTitles";
import { useCompanyStatusOptions } from "../../data/fallbackPayloads/useCompanyStatusOptions";
import { useCountries } from "../../data/useCountries";
import { useCreateProfile } from "../../data/useCreateProfile";
import { useEntityTypes } from "../../data/useEntityTypes";
import { useGetProfileList } from "../../data/useGetProfileList";
import { useLinkRecordProfile } from "../../data/useLinkRecordProfile";
import { useRetrieveProfile } from "../../data/useRetrieveProfile";
import { queryClient } from "../../queryClient";
import {
  CreateProfileRequest,
  Profile,
  ProfileType,
} from "../../types/Profile";
import { MIN_AGE_APPLICATION } from "../../utils/constants";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { useAuth } from "../../context/AuthContext";

type RecordType = "application" | "screening" | "monitoring";

// SEG permission codename gating the "Link profile" action, keyed off the
// module this widget is mounted in (recordType already tells us that).
const LINK_PROFILE_PERMISSION: Record<RecordType, string> = {
  application: "mandate.linkprofile",
  screening: "screening.linkprofile",
  monitoring: "monitoring.linkprofile",
};

type ProfileRecordWidgetProps = {
  record: any;
  recordId?: number;
  recordType: RecordType;
  subjectType: ProfileType;
  onLinked?: () => void;
};

type ProfileFormValues = {
  partyType: ProfileType;
  profile: number | string | null;
  title: string;
  yourReference: string;
  gender: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  name: string;
  country: number | null;
  entityType: number | null;
  status: number | null;
  registrationNumber: string;
  dateOfRegistration: string;
};

const CREATE_NEW_OPTION: Option = {
  label: "Create New Profile",
  value: "CREATE_NEW_PROFILE",
};

const PARTY_TYPE_OPTIONS: Option[] = [
  { label: "Natural person", value: "individual" },
  { label: "Entity", value: "entity" },
];

const getId = (value: any) =>
  typeof value === "object" && value !== null ? value.id : value;

const getProfileName = (profile: any, subjectType: ProfileType) =>
  subjectType === "entity"
    ? profile?.name || profile?.company_name || "-"
    : [profile?.first_name, profile?.middle_name, profile?.last_name]
        .filter(Boolean)
        .join(" ") ||
      profile?.name ||
      "-";

const getRecordName = (record: any, subjectType: ProfileType) =>
  subjectType === "entity"
    ? record?.company_name || record?.profile?.name || record?.name || ""
    : [record?.first_name, record?.middle_name, record?.last_name]
        .filter(Boolean)
        .join(" ");

const getDateValue = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("yyyy-MM-dd") : "";

const getInitialValues = (
  record: any,
  subjectType: ProfileType,
): ProfileFormValues => ({
  partyType: subjectType,
  profile: null,
  title: record?.title || "",
  yourReference: record?.external_reference || "",
  gender: record?.gender || "",
  firstName: record?.first_name || "",
  middleName: record?.middle_name || "",
  lastName: record?.last_name || "",
  dateOfBirth: getDateValue(record?.date_of_birth),
  email: record?.email || "",
  name: getRecordName(record, "entity"),
  country: getId(record?.country) ?? null,
  entityType: getId(record?.entity_type) ?? null,
  status: getId(record?.company_status) ?? null,
  registrationNumber: record?.registration_number || "",
  dateOfRegistration: getDateValue(record?.date_of_incorporation),
});

const buildCreatePayload = (
  values: ProfileFormValues,
  subjectType: ProfileType,
  record: any,
): CreateProfileRequest => {
  if (subjectType === "entity") {
    return {
      _type: "entity",
      name: values.name,
      registration_number: values.registrationNumber || null,
      date_of_incorporation: values.dateOfRegistration || null,
      country: values.country ?? null,
      entity_type: values.entityType ?? null,
      company_status: values.status ?? null,
      external_reference: values.yourReference || null,
      default: false,
      relation: [],
    };
  }

  const fullName = [values.firstName, values.middleName, values.lastName]
    .filter(Boolean)
    .join(" ");

  return {
    _type: "individual",
    name: fullName || null,
    title: values.title || null,
    first_name: values.firstName || "",
    middle_name: values.middleName || null,
    last_name: values.lastName || "",
    date_of_birth: values.dateOfBirth || null,
    email: values.email || null,
    gender: values.gender || null,
    external_reference: values.yourReference || null,
    default: false,
    relation: [],
  };
};

export const ProfileRecordWidget = ({
  record,
  recordId,
  recordType,
  subjectType,
  onLinked,
}: ProfileRecordWidgetProps) => {
  const { data: billingData, isLoading: isLoadingBilling } = useBillingInfo();
  const billingInfo =
    billingData?.data?.results?.length > 0 ? billingData.data.results[0] : null;
  const profilesEnabled = Boolean(billingInfo?.profiles_enabled);

  const { hasPermission } = useAuth();
  // Linking means picking from the profile list, and the linked profile's
  // details and page come from the profile API, all of which 403 (and so log
  // the user out) without profile.view.
  const canViewProfiles = hasPermission("profile.view");
  const canCreateProfile = hasPermission("profile.create");
  const canLinkProfile =
    hasPermission(LINK_PROFILE_PERMISSION[recordType]) && canViewProfiles;
  const linkProfileDeniedMessage = !hasPermission(
    LINK_PROFILE_PERMISSION[recordType],
  )
    ? "You don't have permission to link a profile"
    : "You don't have permission to view profiles";

  const linkedProfile =
    typeof record?.profile === "object" && record?.profile !== null
      ? (record.profile as Profile)
      : undefined;
  const linkedProfileId =
    linkedProfile?.id ??
    (typeof record?.profile === "number" ? record.profile : undefined);
  const linkedSubjectType = linkedProfile?._type ?? subjectType;
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [step, setStep] = useState<"select" | "create">("select");

  // Only needed once the Select Profile slider is open.
  const { data: profileList } = useGetProfileList(
    {
      type: subjectType,
      page: 1,
      pageSize: 100,
    },
    { enabled: isSliderOpen && canLinkProfile },
  );
  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();
  const { data: titlesData } = useCommonTitles();
  const { options: companyStatusOptions } = useCompanyStatusOptions();
  const { mutate: linkRecordProfile, isLoading: isLinking } =
    useLinkRecordProfile();
  const { mutate: createProfile, isLoading: isCreating } = useCreateProfile();

  const profileOptions = useMemo(() => {
    const options =
      profileList?.data?.results?.map((profile) => ({
        label: getProfileName(profile, subjectType),
        value: profile.id,
      })) ?? [];

    return [
      { ...CREATE_NEW_OPTION, isDisabled: !canCreateProfile },
      ...options,
    ];
  }, [profileList, subjectType, canCreateProfile]);

  const countryOptions =
    countries?.data?.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const entityTypeOptions =
    entityTypes?.data?.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const titleOptions =
    titlesData?.data?.map(({ name }: any) => ({
      label: name,
      value: name,
    })) ?? [];

  const genderOptions = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "other", label: "Other" },
  ];

  // The record's embedded `profile` object is a partial snapshot from the
  // record's own serializer and doesn't always carry external_reference, so
  // fetch the full profile to source the reference fields reliably.
  const { data: fullProfile } = useRetrieveProfile(linkedProfileId, {
    enabled: Boolean(linkedProfileId) && canViewProfiles,
  });

  const profileLinkLabel = linkedProfile
    ? getProfileName(linkedProfile, linkedSubjectType)
    : "View profile";
  const profileOurReference =
    fullProfile?.reference ||
    (linkedProfile as any)?.reference ||
    (linkedProfileId ? String(linkedProfileId) : "-");
  const profileYourReference =
    fullProfile?.external_reference || linkedProfile?.external_reference || "-";

  const openSelectSlider = () => {
    setStep("select");
    setIsSliderOpen(true);
  };

  const closeSlider = () => {
    setIsSliderOpen(false);
    setStep("select");
  };

  const handleLinked = () => {
    queryClient.invalidateQueries(["individual"]);
    queryClient.invalidateQueries(["monitoringDetail"]);
    queryClient.invalidateQueries(["profileList"]);
    onLinked?.();
  };

  const handleLinkProfile = (profileId: number) => {
    if (!recordId) return;

    linkRecordProfile(
      { recordId, profileId, recordType },
      {
        onSuccess: () => {
          notify("Profile linked successfully", { type: "success" });
          closeSlider();
          handleLinked();
        },
        onError: (err) =>
          showError(err, "A problem occurred while linking the profile."),
      },
    );
  };

  const handleCreateProfile = (values: ProfileFormValues) => {
    if (!recordId) return;

    createProfile(buildCreatePayload(values, subjectType, record), {
      onSuccess: (res: any) => {
        const profileId = res?.data?.id ?? res?.data?.data?.id;

        if (profileId) {
          handleLinkProfile(profileId);
          return;
        }

        notify("Profile created", { type: "success" });
        closeSlider();
        handleLinked();
      },
      onError: (err) =>
        showError(err, "A problem occurred while creating the profile."),
    });
  };

  if (isLoadingBilling || !profilesEnabled) {
    return null;
  }

  return (
    <Box mb={3}>
      <H3 fontSize={2} mb={1}>
        Profile Reference
      </H3>

      {linkedProfileId ? (
        <Box>
          <RouteLink
            to={`/profile/${linkedProfileId}`}
            fontSize={1}
            fontWeight={600}
            style={{
              display: "inline-block",
              textDecoration: "none",
              opacity: canViewProfiles ? 1 : 0.4,
              cursor: canViewProfiles ? "pointer" : "not-allowed",
            }}
            onClick={(e) => {
              if (!canViewProfiles) e.preventDefault();
            }}
            title={
              canViewProfiles
                ? undefined
                : "You don't have permission to view profiles"
            }
          >
            {profileLinkLabel}
          </RouteLink>
          <Paragraph color="gray.60" fontSize={1} mt={1} mb={0}>
            Our reference: {profileOurReference}
          </Paragraph>
          <Paragraph color="gray.60" fontSize={1} mt={0} mb={0}>
            Your Reference: {profileYourReference}
          </Paragraph>
        </Box>
      ) : (
        <Box>
          <Paragraph color="gray.60" fontSize={1} my={0}>
            No profile linked
          </Paragraph>
          <Flex mt={1} gap="8" flexWrap="wrap">
            <Button
              size="regular"
              variant="text"
              // the text variant doesn't grey itself out when disabled
              color={canLinkProfile ? "blue.primary" : "gray.40"}
              fontWeight={600}
              Icon={FaPlus}
              iconSize="12px"
              iconColor={canLinkProfile ? "blue.primary" : "gray.40"}
              px={0}
              onClick={openSelectSlider}
              isDisabled={!canLinkProfile}
              title={canLinkProfile ? undefined : linkProfileDeniedMessage}
            >
              Link profile
            </Button>
          </Flex>
        </Box>
      )}

      <Slider isOpen={isSliderOpen} onRequestClose={closeSlider} width="600px">
        <Box overflowY="auto" height="calc(100vh - 211px)">
          <Formik
            enableReinitialize
            initialValues={getInitialValues(record, subjectType)}
            onSubmit={handleCreateProfile}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                {step === "select" && (
                  <Box px={4} py={3}>
                    <H2>Select Profile</H2>
                    <Paragraph>
                      Please select the profile you want to link to this record
                    </Paragraph>

                    <Select
                      label="Natural Person or Entity?"
                      name="partyType"
                      value={
                        PARTY_TYPE_OPTIONS.find(
                          (o) => o.value === subjectType,
                        ) ?? null
                      }
                      onChange={() => null}
                      options={PARTY_TYPE_OPTIONS}
                      isDisabled
                      isRequired
                      mt={5}
                    />

                    <Select
                      label="Search Profile"
                      name="profile"
                      value={
                        profileOptions.find(
                          (o) => o.value === props.values.profile,
                        ) ?? null
                      }
                      onChange={(option: Option | Option[]) => {
                        if (Array.isArray(option) || !option) return;

                        if (option.value === CREATE_NEW_OPTION.value) {
                          props.setFieldValue("profile", option.value);
                          setStep("create");
                          return;
                        }

                        props.setFieldValue("profile", option.value);
                      }}
                      options={profileOptions}
                      placeholder="Search"
                      isRequired
                      mt={5}
                    />
                  </Box>
                )}

                {step === "create" && subjectType === "individual" && (
                  <Box px={4} py={3}>
                    <H2>Add New Person</H2>
                    <Paragraph>
                      Create a new individual profile and link it to this record
                    </Paragraph>

                    <Input
                      placeholder="Your Reference"
                      name="yourReference"
                      value={props.values.yourReference}
                      onChange={props.handleChange}
                      label="Your reference"
                      mb={3}
                    />

                    <Flex gap="16" mb={3} flexWrap="wrap">
                      <Box flex={1} minWidth={200}>
                        <Select
                          options={titleOptions}
                          placeholder="Select title"
                          value={
                            titleOptions.find(
                              (o) => o.value === props.values.title,
                            ) ?? null
                          }
                          onChange={(option: Option) =>
                            props.setFieldValue("title", option.value)
                          }
                          name="title"
                          label="Title"
                        />
                      </Box>
                      <Box flex={1} minWidth={200}>
                        <Select
                          options={genderOptions}
                          placeholder="Select gender"
                          value={
                            genderOptions.find(
                              (o) => o.value === props.values.gender,
                            ) ?? null
                          }
                          onChange={(option: Option) =>
                            props.setFieldValue("gender", option.value)
                          }
                          name="gender"
                          label="Gender"
                        />
                      </Box>
                    </Flex>

                    <Flex gap="16" mb={3} flexWrap="wrap">
                      <Box flex={1} minWidth={200}>
                        <Input
                          placeholder="Enter first name"
                          name="firstName"
                          value={props.values.firstName}
                          onChange={props.handleChange}
                          isRequired
                          label="First name"
                        />
                      </Box>
                      <Box flex={1} minWidth={200}>
                        <Input
                          placeholder="Enter middle name(s)"
                          name="middleName"
                          value={props.values.middleName}
                          onChange={props.handleChange}
                          label="Middle name(s)"
                        />
                      </Box>
                    </Flex>

                    <Flex gap="16" mb={3} flexWrap="wrap">
                      <Box flex={1} minWidth={200}>
                        <Input
                          placeholder="Enter last name"
                          name="lastName"
                          value={props.values.lastName}
                          onChange={props.handleChange}
                          isRequired
                          label="Last name"
                        />
                      </Box>
                      <Box flex={1} minWidth={200}>
                        <DatePicker
                          label="Date of birth"
                          value={props.values.dateOfBirth as any}
                          onChange={(date: any) =>
                            props.setFieldValue("dateOfBirth", date)
                          }
                          maxDate={DateTime.now()
                            .minus({ years: MIN_AGE_APPLICATION })
                            .toJSDate()}
                          name="dateOfBirth"
                        />
                      </Box>
                    </Flex>

                    <Input
                      placeholder="Enter email address"
                      name="email"
                      value={props.values.email}
                      onChange={props.handleChange}
                      label="Email address"
                    />
                  </Box>
                )}

                {step === "create" && subjectType === "entity" && (
                  <Box px={4} py={3}>
                    <H2>New Entity</H2>
                    <Paragraph>
                      Create a new entity profile and link it to this record
                    </Paragraph>

                    <Input
                      placeholder="Entity name"
                      name="name"
                      value={props.values.name}
                      onChange={props.handleChange}
                      isRequired
                      label="Entity name"
                    />

                    <Input
                      placeholder="Your Reference"
                      name="yourReference"
                      value={props.values.yourReference}
                      onChange={props.handleChange}
                      label="Your reference"
                      mt={3}
                    />

                    <Select
                      value={
                        countryOptions.find(
                          (o) => o.value === props.values.country,
                        ) ?? null
                      }
                      options={countryOptions}
                      onChange={(option: Option) =>
                        props.setFieldValue("country", option.value)
                      }
                      label="Country of registration/establishment"
                      name="country"
                      mb={3}
                      mt={3}
                    />

                    <Flex gap="16" mb={3}>
                      <Box flex={1}>
                        <Select
                          value={
                            entityTypeOptions.find(
                              (o) => o.value === props.values.entityType,
                            ) ?? null
                          }
                          options={entityTypeOptions}
                          onChange={(option: Option) =>
                            props.setFieldValue("entityType", option.value)
                          }
                          label="Entity Type"
                          name="entity_type"
                        />
                      </Box>
                      <Box flex={1}>
                        <Select
                          value={
                            companyStatusOptions.find(
                              (o: Option) => o.value === props.values.status,
                            ) ?? null
                          }
                          options={companyStatusOptions}
                          onChange={(option: Option) =>
                            props.setFieldValue("status", option.value)
                          }
                          label="Current status"
                          name="status"
                        />
                      </Box>
                    </Flex>

                    <Flex gap="16">
                      <Box flex={1}>
                        <Input
                          placeholder="Registration number"
                          label="Registration number"
                          name="registrationNumber"
                          value={props.values.registrationNumber}
                          onChange={props.handleChange}
                        />
                      </Box>
                      <Box flex={1}>
                        <DatePicker
                          label="Date of registration"
                          value={props.values.dateOfRegistration as any}
                          onChange={(date: any) =>
                            props.setFieldValue("dateOfRegistration", date)
                          }
                          maxDate={DateTime.now().toJSDate()}
                          name="dateOfRegistration"
                        />
                      </Box>
                    </Flex>
                  </Box>
                )}

                <Flex justifyContent="space-between" margin={3}>
                  <Button
                    type="button"
                    onClick={() =>
                      step === "select" ? closeSlider() : setStep("select")
                    }
                  >
                    {step === "select" ? "Cancel" : "Back"}
                  </Button>
                  {step === "select" && (
                    <Button
                      type="button"
                      isDisabled={
                        isLinking ||
                        !props.values.profile ||
                        props.values.profile === CREATE_NEW_OPTION.value
                      }
                      onClick={() =>
                        handleLinkProfile(Number(props.values.profile))
                      }
                    >
                      {isLinking ? "Saving..." : "Save"}
                    </Button>
                  )}
                  {step === "create" && (
                    <Button type="submit" isDisabled={isCreating || isLinking}>
                      {isCreating || isLinking ? "Saving..." : "Create"}
                    </Button>
                  )}
                </Flex>
              </form>
            )}
          </Formik>
        </Box>
      </Slider>
    </Box>
  );
};
